"use client";

import { twarn } from "logitool";
import { ContentFile } from "../../lib/types";
import { getContentData, deleteContentData } from "@/utils/api";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ServiceReturn } from "rsper";

type WorkPage = {
  pageId: string;
  pageName: string;
};

export default function WorksPage() {
  const [works, setWorks] = useState<ContentFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedWork, setExpandedWork] = useState<string | null>(null);
  const [deletingWork, setDeletingWork] = useState<string | null>(null);

  const handleDeleteWork = async (workId: string, workKey: string) => {
    if (!confirm('Are you sure you want to delete this work? This action cannot be undone.')) return;
    try {
      setDeletingWork(workKey);
      await deleteContentData(`/publish/work/${workId}`);
      setWorks(prev => prev.filter(w => (w._id || w.workId || '') !== workKey));
      setExpandedWork(null);
    } catch (err) {
      alert('Failed to delete work. Please try again.');
    } finally {
      setDeletingWork(null);
    }
  };

  useEffect(() => {
    const fetchWorks = async () => {
      try {
        setLoading(true);
        const result: ServiceReturn<ContentFile[]> = await getContentData('/publish/works');
        if (result.success && result.data) {
          const normalized = result.data.map((w: any) => ({
            ...w,
            workId: w.workId || w.campaignId,
            workName: w.workName || w.campaignName,
          }));
          setWorks(normalized);
        } else {
          setError('Failed to load works');
        }
      } catch (err) {
        setError('Failed to fetch works');
      } finally {
        setLoading(false);
      }
    };
    fetchWorks();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#01A982] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#444444] text-sm">Loading works...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-full bg-[#FFBC44]/10 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-[#FFBC44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-[#333333] mb-2">Unable to load works</h2>
          <p className="text-[#666666] text-sm mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-6 py-3 bg-[#01A982] text-white text-sm font-medium rounded-lg hover:bg-[#008567] transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <header className="bg-white border-b border-[#CCCCCC]/40">
        <div className="max-w-[1440px] mx-auto">
          <div className="h-[72px] px-6 lg:px-12 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="text-lg font-semibold text-[#333333]">
                Puck Editor
              </div>
              <div className="hidden md:block w-px h-6 bg-[#CCCCCC]/60"></div>
              <nav className="hidden md:flex items-center gap-6">
                <Link
                  href="/works"
                  className="text-sm font-medium text-[#01A982] border-b-2 border-[#01A982] pb-0.5"
                >
                  Works
                </Link>
                <Link
                  href="/design-sources"
                  className="text-sm font-medium text-[#555555] hover:text-[#01A982] transition-colors"
                >
                  Design Sources
                </Link>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2 w-64 h-10 px-4 bg-[#F2F2F2] rounded-lg border border-transparent focus-within:border-[#01A982] focus-within:bg-white transition-all">
                <svg className="w-4 h-4 text-[#999999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search works"
                  className="flex-1 bg-transparent text-sm text-[#333333] placeholder-[#999999] outline-none"
                />
              </div>

              <Link
                href="/design-sources"
                className="inline-flex items-center gap-2 h-10 px-5 bg-[#01A982] text-white text-sm font-medium rounded-lg hover:bg-[#008567] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">New Work</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-[#CCCCCC]/40">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8 lg:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-semibold text-[#333333] mb-2">Your Works</h1>
              <p className="text-[#666666] text-sm lg:text-base">View and manage your published works</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#666666]">
              <span><strong className="text-[#333333]">{works.length}</strong> works</span>
              <span><strong className="text-[#333333]">{works.reduce((a, c) => a + (c?.pages?.length || 0), 0)}</strong> pages</span>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8 lg:py-12">
        {works.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-[#F2F2F2] flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-[#999999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-[#333333] mb-2">No works yet</h2>
              <p className="text-[#666666] text-sm mb-8">Create your first work by selecting a design source.</p>
              <Link
                href="/design-sources"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#01A982] text-white text-sm font-medium rounded-lg hover:bg-[#008567] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Work
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 items-start">
            {works.map((work) => {
              const workKey = work._id || work.workId || '';
              const isExpanded = expandedWork === workKey;
              const pageCount = work.pages?.length || 0;

              return (
                <div
                  key={workKey}
                  className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden hover:border-[#01A982]/40 hover:shadow-lg hover:shadow-black/5 transition-all duration-200"
                >
                  <Link href={`/edit-work/${work.workId}`} className="block group">
                    <div
                      className="relative bg-[#FAFAFA] border-b border-[#E0E0E0]"
                      style={{ aspectRatio: '16 / 10' }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-xl bg-white border border-[#E0E0E0] flex items-center justify-center">
                          <svg className="w-7 h-7 text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                      </div>

                      <div className="absolute inset-0 bg-[#01A982]/0 group-hover:bg-[#01A982]/90 transition-colors duration-200 flex items-center justify-center">
                        <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          Edit Work
                        </span>
                      </div>

                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 bg-white/95 rounded-md text-xs font-medium text-[#555555] shadow-sm border border-black/5">
                          {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                        </span>
                      </div>
                    </div>
                  </Link>

                  <div className="p-5">
                    <h3 className="text-[15px] font-semibold text-[#333333] truncate mb-1">
                      {work.workName}
                    </h3>
                    <p className="text-xs text-[#888888] mb-4">
                      {work.updatedAt
                        ? `Updated ${new Date(work.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        : work.createdAt
                          ? `Created ${new Date(work.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : 'No date'}
                    </p>

                    <div className="flex gap-2 mb-4">
                      <Link
                        href={`/edit-work/${work.workId}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-[#01A982] hover:bg-[#008567] rounded-lg text-sm text-white font-medium transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </Link>
                    </div>

                    <button
                      onClick={() => setExpandedWork(isExpanded ? null : workKey)}
                      className="w-full flex items-center justify-between py-2.5 px-4 bg-[#F7F7F7] hover:bg-[#EEEEEE] rounded-lg text-sm text-[#555555] transition-colors cursor-pointer"
                    >
                      <span className="font-medium">{isExpanded ? 'Hide details' : 'View details'}</span>
                      <svg
                        className={`w-4 h-4 text-[#888888] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-[#E0E0E0]">
                        <div className="space-y-2.5 mb-5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-[#888888]">_id</span>
                            <code className="text-xs text-[#555555] bg-[#F7F7F7] px-2 py-0.5 rounded max-w-[180px] truncate">{work._id || '-'}</code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-[#888888]">workId</span>
                            <code className="text-xs text-[#555555] bg-[#F7F7F7] px-2 py-0.5 rounded max-w-[180px] truncate">{work.workId || '-'}</code>
                          </div>
                        </div>

                        {work.pages && work.pages.length > 0 && (
                          <div>
                            <div className="text-xs text-[#888888] mb-2">Pages</div>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {work.pages.map((page, index) => (
                                <div
                                  key={page.pageId || index}
                                  className="flex items-center gap-3 p-2.5 bg-[#FAFAFA] rounded-lg border border-transparent"
                                >
                                  <div className="w-7 h-7 rounded-md bg-white border border-[#E0E0E0] flex items-center justify-center text-xs font-semibold text-[#666666]">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[#444444] truncate">
                                      {page.pageName || `Page ${index + 1}`}
                                    </p>
                                    <p className="text-[10px] text-[#AAAAAA] truncate mt-0.5 font-mono">
                                      {page.pageId}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => handleDeleteWork(work.workId || '', workKey)}
                          disabled={deletingWork === workKey}
                          className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 px-4 bg-white border border-red-300 hover:bg-red-50 hover:border-red-400 rounded-lg text-sm text-red-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {deletingWork === workKey ? (
                            <>
                              <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete Work
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-[#E0E0E0] bg-white mt-auto">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-center justify-between text-xs text-[#888888]">
            <span>Puck Editor</span>
            <span>{works.length} works available</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
